import { useEffect, useState } from 'react'
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Divider, Link,
  List, ListItem, ListItemText, Typography,
} from '@mui/material'
import { CheckCircle, RemoveCircleOutline } from '@mui/icons-material'
import { entitlementsApi, TenantEntitlements } from '../services/api'
import useCanContactVendor from '../hooks/useCanContactVendor'

/**
 * Read-only view of the features provisioned for this tenant.
 *
 * Deliberately has no toggles. These features are provisioned per
 * contract by a platform admin; tenant admins can see what they have but
 * change it through support. Rendering a disabled switch would imply the
 * control exists and is merely unavailable to this user, which is the
 * wrong mental model — so the state is shown as a status chip instead.
 */
const TenantFeaturesPanel = ({ tenantId }: { tenantId: string }) => {
  const [data, setData] = useState<TenantEntitlements | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!tenantId) return
    setLoading(true)
    entitlementsApi
      .get(tenantId)
      .then((resp) => {
        setData(resp.data)
        setError(null)
      })
      .catch((err) => {
        const code = err?.response?.status
        if (code === 403) {
          setError('You do not have access to this tenant’s features.')
        } else if (code === 404) {
          setError(null)
          setData(null)
        } else {
          setError('Could not load your feature list. Please try again shortly.')
        }
      })
      .finally(() => setLoading(false))
  }, [tenantId])

  const support = data?.support_contact || 'support@tryarias.com'
  const anyEnabled = (data?.features || []).some((f) => f.enabled)
  const canContactVendor = useCanContactVendor()

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Your Features
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Features included in your plan.{' '}
          {canContactVendor ? (
            <>
              To add or remove a feature, contact{' '}
              <Link href={`mailto:${support}`}>{support}</Link>.
            </>
          ) : (
            'To add or remove a feature, ask your tenant admin.'
          )}
        </Typography>

        <Divider sx={{ mb: 1 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : error ? (
          <Alert severity="warning">{error}</Alert>
        ) : !data || data.features.length === 0 ? (
          <Alert severity="info">
            No additional features are configured for this tenant.{' '}
            {canContactVendor ? (
              <>
                Contact <Link href={`mailto:${support}`}>{support}</Link> to
                discuss what’s available on your plan.
              </>
            ) : (
              'Ask your tenant admin what is available on your plan.'
            )}
          </Alert>
        ) : (
          <>
            {!anyEnabled && (
              <Alert severity="info" sx={{ mb: 2 }}>
                None of these are currently active on your plan.{' '}
                {canContactVendor ? (
                  <>
                    Contact <Link href={`mailto:${support}`}>{support}</Link> to
                    enable them.
                  </>
                ) : (
                  'Ask your tenant admin to enable them.'
                )}
              </Alert>
            )}
            <List dense disablePadding>
              {data.features.map((feature) => (
                <ListItem key={feature.key} disableGutters sx={{ py: 1 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">{feature.label}</Typography>
                        <Chip
                          size="small"
                          icon={
                            feature.enabled ? (
                              <CheckCircle fontSize="small" />
                            ) : (
                              <RemoveCircleOutline fontSize="small" />
                            )
                          }
                          label={feature.enabled ? 'Enabled' : 'Not enabled'}
                          color={feature.enabled ? 'success' : 'default'}
                          variant={feature.enabled ? 'filled' : 'outlined'}
                        />
                      </Box>
                    }
                    secondary={feature.description}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default TenantFeaturesPanel
